/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState } from "recoil";
import { updateUser } from "../../../api/User";
import { UserAtom } from "../../../atoms/UserAtom";
import StyledButton from "../../StyledButton";
import StyledInput from "../../StyledInput";
import { useModal } from "../Modal";

const ModalBody = styled.div`
  width: 390px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #eceeee;

  margin: 16px -24px 0px;
`;

const Wrapper = styled.div`
  width: 100%;
  height: fit-content;

  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;

  padding: 32px 24px;

  > div + div {
    margin-top: 24px;
  }
`;

const Buttons = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: flex-end;

  border-top: 1px solid #eceeee;
  padding: 24px 24px 0px;
`;

const InputLabel = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const Title = styled.div`
  color: #2f2f2f;
  font-weight: 600;
  margin-bottom: 12px;
`;

export default function UpdateAppName(): JSX.Element | null {
  const [isLoading, setLoading] = useState(false);
  const [user, setUser] = useRecoilState(UserAtom);
  const [userAppName, setUserAppName] = useState<string>(user.userAppName);
  const modal = useModal();

  function handleChangeInput(event: React.ChangeEvent<HTMLInputElement>) {
    setUserAppName(event.target.value.trim().toLocaleLowerCase());
  }

  async function sendUpdateUser() {
    if (!user) return false;

    setLoading(true);

    const userObject = {
      userAppName: userAppName,
    };

    const success = await updateUser(userObject);

    if (success) {
      setUser({
        ...user,
        userAppName: userAppName,
      });
      toast("App name updated", { type: "success" });
    } else {
      toast("An user with this App Name already exists", { type: "error" });
    }
    modal.popModal();
    setLoading(false);

    return;
  }

  useEffect(() => {
    if (!user) return;
    const listener = (event: KeyboardEvent) => {
      if (event.code === "Enter" || event.code === "NumpadEnter") {
        event.preventDefault();
        sendUpdateUser();
      }
    };
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, [user.userAppName]);

  if (!user) return null;

  return (
    <ModalBody>
      <Wrapper>
        <InputLabel>
          <Title>New Unique App Name</Title>
          <StyledInput
            placeholder="Enter unique app name"
            onChange={handleChangeInput}
            value={userAppName}
          />
        </InputLabel>
      </Wrapper>

      <Buttons>
        <StyledButton
          inverted
          css={css`
            width: 80px;
            margin-right: 10px;
          `}
          onClick={() => modal.popModal()}
        >
          Cancel
        </StyledButton>
        <StyledButton
          disable={userAppName === user.userAppName || userAppName.length === 0}
          isLoading={isLoading}
          css={css`
            width: 80px;
          `}
          onClick={() => sendUpdateUser()}
        >
          Save
        </StyledButton>
      </Buttons>
    </ModalBody>
  );
}
